/**
 * BikeComparisonDescentsGrid — side-by-side descent comparison table.
 */
import { Box, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';

const Cell = ({ color, children }) => (
  <Box display="flex" alignItems="center" height="100%">
    <Typography variant="body2" color={color}>{children}</Typography>
  </Box>
);

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function fmtDur(s) {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}
function fmtSpd(v) { return v == null ? '—' : `${v.toFixed(1)} km/h`; }
function fmtPwr(v) { return v == null ? '—' : `${Math.round(v)} W`; }
function fmtDelta(s) {
  if (s == null) return '—';
  const sign = s > 0 ? '+' : '';
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = Math.floor(abs % 60);
  return `${sign}${m}:${sec.toString().padStart(2, '0')}`;
}

export default function BikeComparisonDescentsGrid({ commonDescents }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!commonDescents || commonDescents.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color={colors.grey[400]}>No common descents detected</Typography>
      </Box>
    );
  }

  const rows = commonDescents.map((d, idx) => ({
    id: idx,
    category: d.category,
    start_km: (d.start_distance_m / 1000).toFixed(1),
    length_m: Math.round(d.length_m),
    loss_m: d.elevation_loss_m != null ? Math.round(d.elevation_loss_m) : null,
    grad_pct: d.avg_gradient_pct,
    dur_a: d.a?.duration_s,
    dur_b: d.b?.duration_s,
    spd_a: d.a?.avg_speed,
    spd_b: d.b?.avg_speed,
    pwr_a: d.a?.avg_power,
    pwr_b: d.b?.avg_power,
    time_delta: d.time_delta_s,
  }));

  const columns = [
    { field: 'category', headerName: 'Cat', width: 80 },
    { field: 'start_km', headerName: 'Start', width: 70, valueFormatter: v => `${v} km` },
    { field: 'length_m', headerName: 'Length', width: 80, valueFormatter: v => `${v} m` },
    { field: 'loss_m', headerName: 'Loss', width: 70, valueFormatter: v => v == null ? '—' : `${v} m` },
    { field: 'grad_pct', headerName: 'Grad', width: 70, valueFormatter: v => v == null ? '—' : `${v.toFixed(1)}%` },
    { field: 'dur_a', headerName: 'Time A', width: 80, renderCell: ({ value }) => <Cell color={COLOR_A}>{fmtDur(value)}</Cell> },
    { field: 'dur_b', headerName: 'Time B', width: 80, renderCell: ({ value }) => <Cell color={COLOR_B}>{fmtDur(value)}</Cell> },
    {
      field: 'time_delta', headerName: 'Δ Time', width: 80,
      renderCell: ({ value }) => {
        const col = value == null ? '#9ca3af' : value > 0 ? '#f59e0b' : '#22c55e';
        return <Cell color={col}>{fmtDelta(value)}</Cell>;
      },
    },
    { field: 'spd_a', headerName: 'Spd A', width: 90, renderCell: ({ value }) => <Cell color={COLOR_A}>{fmtSpd(value)}</Cell> },
    { field: 'spd_b', headerName: 'Spd B', width: 90, renderCell: ({ value }) => <Cell color={COLOR_B}>{fmtSpd(value)}</Cell> },
    { field: 'pwr_a', headerName: 'Pwr A', width: 80, renderCell: ({ value }) => <Cell color={COLOR_A}>{fmtPwr(value)}</Cell> },
    { field: 'pwr_b', headerName: 'Pwr B', width: 80, renderCell: ({ value }) => <Cell color={COLOR_B}>{fmtPwr(value)}</Cell> },
  ];

  return (
    <Box sx={{ height: Math.min(400, 56 + rows.length * 52 + 52), minHeight: 200 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        hideFooter
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[800] },
          '& .MuiDataGrid-cell': { borderColor: colors.grey[700] },
          '& .MuiDataGrid-footerContainer': { borderColor: colors.grey[700] },
        }}
      />
    </Box>
  );
}
